export const REFERENCE_FONT_SIZE_PIXELS = 16;

export const CARD_DEFAULTS = Object.freeze({
    previewWordLimit: 50,
    cardHeightPixels: 480,
    columns: 3
});

const LIMITS = Object.freeze({
    previewWordLimit: [10, 150],
    cardHeightPixels: [320, 800],
    columns: [1, 4]
});

/** Keep separately embedded grids within the same limits as the CMS service. */
export function normalizeSettings(settings = {}) {
    return Object.fromEntries(Object.entries(CARD_DEFAULTS).map(([key, fallback]) => {
        const value = settings?.[key];
        const [minimum, maximum] = LIMITS[key];
        return [key, typeof value === 'number' && Number.isFinite(value)
            ? Math.min(maximum, Math.max(minimum, Math.trunc(value))) : fallback];
    }));
}

export function previewText(summary, wordLimit) {
    const words = (typeof summary === 'string' ? summary.trim() : '').split(/\s+/u).filter(Boolean);
    return words.length > wordLimit ? `${words.slice(0, wordLimit).join(' ')}…` : words.join(' ');
}

/** Resource links may open email/phone handlers; images must use HTTP(S). */
export function safeCardUrl(value, { link = false } = {}) {
    if (typeof value !== 'string' || !value.trim()) return null;

    try {
        const decoded = decodeURIComponent(value);
        // Reject browser-normalized obfuscation before URL resolves a relative path.
        if (/[\\\u0000-\u001f\u007f]/u.test(value) || /[\\\u0000-\u001f\u007f]/u.test(decoded)
            || value.trim().startsWith('//') || decoded.trim().startsWith('//')) return null;

        const url = new URL(value.trim(), window.location.href);
        if (url.username || url.password) return null;
        if (['http:', 'https:'].includes(url.protocol)) return url.href;
        if (link && ['mailto:', 'tel:'].includes(url.protocol) && url.pathname.trim()
            && !value.trim().slice(url.protocol.length).startsWith('//')) return url.href;
    } catch {
        return null;
    }
    return null;
}

export function normalizeCard(card) {
    if (!card || typeof card.id !== 'string' || !card.id.trim() || typeof card.title !== 'string') {
        throw new TypeError('A card must have a string identifier and title.');
    }

    return {
        id: card.id,
        title: card.title.trim() || 'Untitled card',
        imageUrl: safeCardUrl(card.imageUrl),
        imageAlt: typeof card.imageAlt === 'string' ? card.imageAlt : '',
        summary: typeof card.summary === 'string' ? card.summary : '',
        // These two fields must come from the server's HTML sanitizer. The client
        // preserves their formatting; it is not a substitute for that sanitizer.
        bodyHtml: typeof card.bodyHtml === 'string' ? card.bodyHtml : '',
        footerHtml: typeof card.footerHtml === 'string' ? card.footerHtml : '',
        printUrl: safeCardUrl(card.printUrl),
        linkUrl: safeCardUrl(card.linkUrl, { link: true }),
        linkLabel: typeof card.linkLabel === 'string' && card.linkLabel.trim() ? card.linkLabel : 'Visit resource'
    };
}

/** Reject malformed server data before replacing any working HTML fallback. */
export function normalizePayload(payload) {
    if (!payload || !Array.isArray(payload.items)) {
        throw new TypeError('The card grid payload must contain an items array.');
    }

    const items = payload.items.map(normalizeCard);
    if (new Set(items.map(card => card.id)).size !== items.length) {
        throw new TypeError('Card identifiers must be unique within a grid.');
    }

    return {
        items,
        settings: normalizeSettings(payload.settings),
        heading: typeof payload.heading === 'string' ? payload.heading : ''
    };
}
